import { Component, computed, effect, inject, input, OnInit, output, Signal, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { EFieldTypes } from 'scw-digital-boards-widgets';
import { TReducerState } from '../../../../app.config';
import { WorkspaceModuleService } from '../../../../core/services/workspace-module.service';
import { IWorkspaceModules } from '../../../../pages/settings/dashboards/dashboards.interface';
import { IDropdownOption } from '../../mat-dropdown/mat-dropdown.component';
import { ADVANCED_FILTER_FIELD_MODULES } from '../advanced-filter-fields.constants';
import { AdvancedFilterInputsComponent } from '../advanced-filter-inputs/advanced-filter-inputs.component';
import {
  EAdvancedFilterInputMode,
  TAdvancedFilterInputsAutoSubmit,
} from '../advanced-filter-inputs/advanced-filter-inputs.interface';
import { IFilterIdentity, TFilterSaveWithOnHold } from '../advanced-filter.interface';
import { AdvancedFilterService } from '../advanced-filter.service';

type TWorkspaceModules = keyof IWorkspaceModules | 'independent' | 'custom';

@Component({
  imports: [MatIcon, AdvancedFilterInputsComponent, MatMenuItem, MatMenu, MatMenuTrigger, TranslatePipe, MatButton],
  selector: 'advanced-filter-inputs-toggle-chip',
  styleUrl: './advanced-filter-inputs-toggle-chip.component.scss',
  templateUrl: './advanced-filter-inputs-toggle-chip.component.html',
})
export class AdvancedFilterInputsToggleChipComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly globalStore = inject<Store<TReducerState>>(Store);
  public readonly workspaceModuleService = inject(WorkspaceModuleService);
  public readonly advancedFilterService = inject(AdvancedFilterService);

  public filterItemId = Symbol();
  public readonly oneEditOperationAtATimeSubject = input<Subject<symbol> | null>(null);
  public selectedFilterIdentity: IFilterIdentity | null = null;

  public readonly mode = signal<'button' | 'dropdowns'>('button');
  public readonly initialField = signal<string | undefined>(undefined);
  public readonly addFilter = output<TFilterSaveWithOnHold>();
  public readonly hoveredFilterIdentityUpdate = output<IFilterIdentity | null>();
  public readonly makeOnHoldChipVisible = output<symbol>();
  public readonly deleteOnHoldChip = output<symbol>();

  public readonly EAdvancedFilterInputMode = EAdvancedFilterInputMode;
  public readonly workspaceModules = this.globalStore.selectSignal(
    ({ user }) => user.user?.authorizations?.workspaceModules,
  );
  public readonly fieldMenuOptions: Signal<Record<TWorkspaceModules, IDropdownOption<string>[]>> = computed(() => {
    return (['independent', 'oeeTracker', 'actionTracker', 'custom'] as TWorkspaceModules[]).reduce(
      (reducePayload, module) => {
        const fields =
          module === 'custom'
            ? this.advancedFilterService.manager
                .configuration()
                .filter(({ idOfCustom }) => Boolean(idOfCustom))
                .map(({ field }) => field)
            : Object.entries(ADVANCED_FILTER_FIELD_MODULES)
                .filter(([, modules]) => (module === 'independent' ? !modules.length : modules.includes(module)))
                .map(([moduleName]) => moduleName);

        return {
          ...reducePayload,
          [module]: fields.map((field) => ({
            id: field,
            name: module === 'custom' ? field : this.translate.instant(`advanceFilter.field.${field}`),
          })),
        };
      },
      {} as Record<TWorkspaceModules, IDropdownOption<string>[]>,
    );
  });

  public lastAutosubmit: TAdvancedFilterInputsAutoSubmit = { data: null, isValid: false };

  constructor() {
    effect(() => {
      const oneEditOperationAtATimeSubject = this.oneEditOperationAtATimeSubject();

      if (this.mode() === 'dropdowns' && oneEditOperationAtATimeSubject) {
        oneEditOperationAtATimeSubject.next(this.filterItemId);
      }
    });

    effect(() => {
      if (this.mode() === 'button') {
        this.makeOnHoldChipVisible.emit(this.filterItemId);
      } else if (this.mode() === 'dropdowns') {
        this.filterItemId = Symbol();
      }
    });
  }

  public ngOnInit(): void {
    const oneEditOperationAtATimeSubject = this.oneEditOperationAtATimeSubject();

    if (oneEditOperationAtATimeSubject) {
      oneEditOperationAtATimeSubject.subscribe((filterItemId) => {
        if (filterItemId !== this.filterItemId) {
          this.dismissAdd();
        }
      });
    }
  }

  public submit({ isValid, data }: TAdvancedFilterInputsAutoSubmit): void {
    this.lastAutosubmit = { isValid, data } as TAdvancedFilterInputsAutoSubmit;

    if (!isValid || data === null) {
      this.deleteOnHoldChip.emit(this.filterItemId);

      return;
    }

    const isOnHold = [
      EFieldTypes.apiDropdown,
      EFieldTypes.predefined,
      EFieldTypes.customPredefined,
      EFieldTypes.customApiDropdown,
    ].includes(data.type);

    if (!isOnHold) {
      this.mode.set('button');
    }

    this.hoveredFilterIdentityUpdate.emit(null);
    this.addFilter.emit({
      ...data,
      id: this.filterItemId,
      isOnHold,
    });
  }

  public dismissAdd(isCancel = false): void {
    this.mode.set('button');
    this.hoveredFilterIdentityUpdate.emit(null);

    if (isCancel || !this.lastAutosubmit.isValid) {
      this.deleteOnHoldChip.emit(this.filterItemId);
    }
  }

  public initializeAdd(field: string): void {
    if (this.lastAutosubmit.isValid) {
      this.mode.set('button');
    }

    setTimeout(() => {
      this.initialField.set(field);
      this.mode.set('dropdowns');
    });
  }

  public convertToChip(): void {
    this.oneEditOperationAtATimeSubject()?.next(Symbol());
  }
}
